import React, { useState, useEffect, useMemo } from 'react';
import { Download, Loader2, Factory } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { productionAPI } from '../../services/api';
import DateRangeFilter from './DateRangeFilter';
import { toast } from 'sonner';

const COLORS = ['#24549c', '#FF9800', '#4CAF50', '#3a6fc4', '#E65100', '#6A1B9A', '#1a3d73', '#00897B'];
const statusColors = { 'Planned': '#3a6fc4', 'In Progress': '#FF9800', 'Completed': '#4CAF50', 'Dispatched': '#00897B', 'Cancelled': '#E65100' };

const toYMD = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return null;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const fmtQty = (n) => (n || 0).toLocaleString('en-IN');

const ProductionReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('subcategory');
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = {};
        const from = toYMD(dateRange.from); const to = toYMD(dateRange.to);
        if (from) params.from_date = from;
        if (to) params.to_date = to;
        const res = await productionAPI.getBreakdown(params);
        setData(res);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load production report');
      } finally { setLoading(false); }
    };
    fetchData();
  }, [dateRange]);

  const summary = data?.summary || {};
  const bySubcategory = data?.by_subcategory || [];
  const byProduct = data?.by_product || [];
  const byStatus = data?.by_status || [];
  const byBranch = data?.by_branch || [];
  const orders = data?.orders || [];

  const chartData = useMemo(() => {
    if (view === 'subcategory') return bySubcategory;
    if (view === 'status') return byStatus.map(s => ({ name: s.name, produced_qty: s.total_qty }));
    if (view === 'branch') return byBranch;
    return byProduct;
  }, [view, bySubcategory, byProduct, byStatus, byBranch]);

  const exportCSV = () => {
    let rows = [];
    let filename = `production-${view}`;
    if (view === 'list') {
      rows = orders.map(o => ({
        'Prod No': o.production_number, Product: o.product, Qty: o.quantity, Unit: o.unit,
        Status: o.status, Branch: o.branch_name, 'OC No': o.oc_number || '', Date: (o.created_at || '').slice(0, 10),
      }));
    } else if (view === 'status') {
      rows = byStatus.map(s => ({ Status: s.name, Orders: s.count, 'Total Qty': s.total_qty }));
    } else {
      const src = view === 'branch' ? byBranch : view === 'product' ? byProduct : bySubcategory;
      rows = src.map(r => ({
        Name: r.name, Unit: r.unit || '', 'Produced Qty': r.produced_qty, 'Total Qty': r.total_qty,
        'Planned Qty': r.planned_qty, 'In-Progress Qty': r.in_progress_qty,
        'Completed Qty': r.completed_qty, 'Dispatched Qty': r.dispatched_qty,
        Orders: r.orders,
      }));
    }
    if (!rows.length) { toast.warning('Nothing to export'); return; }
    const headers = Object.keys(rows[0]).join(',');
    const body = rows.map(r => Object.values(r).map(v => `"${v ?? ''}"`).join(','));
    const blob = new Blob([[headers, ...body].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${filename}.csv`; a.click();
  };

  return (
    <div data-testid="production-report">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Production Analytics</h2>
          <p className="text-xs text-gray-500">Product-wise produced quantity with status breakdown</p>
        </div>
        <button onClick={exportCSV} data-testid="production-export-csv" className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}><Download className="w-3.5 h-3.5" /> Export CSV</button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
        {[
          { l: 'Total Orders', v: summary.total_orders || 0 },
          { l: 'Produced Qty', v: fmtQty(summary.produced_qty) },
          { l: 'Completed', v: summary.completed || 0 },
          { l: 'In Progress', v: summary.in_progress || 0 },
          { l: 'Planned', v: summary.planned || 0 },
          { l: 'Completion', v: `${summary.completion_rate || 0}%` },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`prod-kpi-${i}`}>
            <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{s.v}</p>
            <p className="text-[10px] text-gray-500">{s.l}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <DateRangeFilter onChange={setDateRange} />
        <div className="flex gap-1.5 ml-auto">
          {[{ id: 'subcategory', label: 'Sub-category-wise' }, { id: 'product', label: 'Product-wise' }, { id: 'status', label: 'Status-wise' }, { id: 'branch', label: 'Branch-wise' }, { id: 'list', label: 'List View' }].map(t => (
            <button key={t.id} onClick={() => setView(t.id)} data-testid={`prod-view-${t.id}`} className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${view === t.id ? 'text-white' : 'text-gray-600 bg-gray-100'}`} style={view === t.id ? { backgroundColor: '#24549c' } : {}}>{t.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="prod-loading"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#24549c' }} /></div>
      ) : (
      <>
      {view !== 'list' && (
        <div className="grid lg:grid-cols-2 gap-5 mb-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">{view === 'subcategory' ? 'Sub-category' : view === 'product' ? 'Product' : view === 'status' ? 'Status' : 'Branch'} — Produced Qty</h3>
            {chartData.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9CA3AF' }} angle={-15} textAnchor="end" height={70} interval={0} />
                  <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                  <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '11px' }} formatter={(v) => fmtQty(v)} />
                  <Bar dataKey="produced_qty" name="Produced Qty" radius={[4, 4, 0, 0]}>
                    {chartData.slice(0, 10).map((d, i) => <Cell key={i} fill={view === 'status' ? (statusColors[d.name] || '#24549c') : COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Distribution</h3>
            {chartData.filter(d => (d.produced_qty || 0) > 0).length === 0 ? <EmptyChart /> : (
              <>
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie data={chartData.filter(d => (d.produced_qty || 0) > 0)} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="produced_qty" nameKey="name">
                      {chartData.filter(d => (d.produced_qty || 0) > 0).map((d, i) => <Cell key={i} fill={view === 'status' ? (statusColors[d.name] || COLORS[i % COLORS.length]) : COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '11px' }} formatter={(v) => fmtQty(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 mt-2">
                  {chartData.filter(d => (d.produced_qty || 0) > 0).slice(0, 12).map((d, i) => (
                    <span key={i} className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: view === 'status' ? (statusColors[d.name] || COLORS[i % COLORS.length]) : COLORS[i % COLORS.length] }} />{d.name}</span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tables */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
        {(view === 'subcategory' || view === 'product') && (
          <table className="w-full text-xs" data-testid={`prod-table-${view}`}>
            <thead><tr className="border-b border-gray-100 bg-gray-50">
              {[view === 'subcategory' ? 'Sub-category' : 'Product', 'Unit', 'Planned Qty', 'In-Progress Qty', 'Completed Qty', 'Dispatched Qty', 'Produced Qty', 'Total Qty', 'Orders'].map(h => <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {(view === 'subcategory' ? bySubcategory : byProduct).length === 0 ? <EmptyRow cols={9} /> : (view === 'subcategory' ? bySubcategory : byProduct).map((r, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-semibold text-gray-900">{r.name}</td>
                  <td className="px-4 py-3 text-gray-400">{r.unit}</td>
                  <td className="px-4 py-3 text-blue-600">{fmtQty(r.planned_qty)}</td>
                  <td className="px-4 py-3 text-amber-600">{fmtQty(r.in_progress_qty)}</td>
                  <td className="px-4 py-3 text-green-600">{fmtQty(r.completed_qty)}</td>
                  <td className="px-4 py-3 text-teal-700">{fmtQty(r.dispatched_qty)}</td>
                  <td className="px-4 py-3 font-semibold text-green-700">{fmtQty(r.produced_qty)}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtQty(r.total_qty)}</td>
                  <td className="px-4 py-3 text-gray-500">{r.orders}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {view === 'status' && (
          <table className="w-full text-xs" data-testid="prod-table-status">
            <thead><tr className="border-b border-gray-100 bg-gray-50">{['Status', 'Orders', 'Total Qty'].map(h => <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
            <tbody>
              {byStatus.length === 0 ? <EmptyRow cols={3} /> : byStatus.map((r, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-semibold"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors[r.name] || '#999' }} />{r.name}</span></td>
                  <td className="px-4 py-3 text-gray-500">{r.count}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtQty(r.total_qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {view === 'branch' && (
          <table className="w-full text-xs" data-testid="prod-table-branch">
            <thead><tr className="border-b border-gray-100 bg-gray-50">{['Branch', 'Produced Qty', 'Total Qty', 'Orders', 'Completed'].map(h => <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
            <tbody>
              {byBranch.length === 0 ? <EmptyRow cols={5} /> : byBranch.map((r, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-semibold text-gray-900">{r.name}</td>
                  <td className="px-4 py-3 font-semibold text-green-700">{fmtQty(r.produced_qty)}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtQty(r.total_qty)}</td>
                  <td className="px-4 py-3 text-gray-500">{r.orders}</td>
                  <td className="px-4 py-3 text-green-600">{r.completed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {view === 'list' && (
          <table className="w-full text-xs" data-testid="prod-table-list">
            <thead><tr className="border-b border-gray-100 bg-gray-50">{['Prod No', 'Product', 'Qty', 'Status', 'Branch', 'OC No', 'Date'].map(h => <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
            <tbody>
              {orders.length === 0 ? <EmptyRow cols={7} /> : orders.map((o, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-mono text-gray-500">{o.production_number}</td>
                  <td className="px-4 py-3 font-semibold text-gray-900">{o.product || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtQty(o.quantity)} {o.unit}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors[o.status] || '#999' }} />{o.status}</span></td>
                  <td className="px-4 py-3 text-gray-500">{o.branch_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{o.oc_number || '—'}</td>
                  <td className="px-4 py-3 text-gray-400">{(o.created_at || '').slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </div>
  );
};

const EmptyChart = () => (
  <div className="flex flex-col items-center justify-center h-[230px] text-gray-300"><Factory className="w-10 h-10 mb-2" /><p className="text-xs">No production data for this range</p></div>
);
const EmptyRow = ({ cols }) => (
  <tr><td colSpan={cols} className="px-4 py-8 text-center text-xs text-gray-400">No production data for this range</td></tr>
);

export default ProductionReport;
